/**
 * useAdImages — the picture and the wording of each ad, for the Ads report.
 *
 * Fetched once and kept for the life of the tab (they change only when an admin saves one,
 * and that save updates this copy), so moving between pages or periods never re-downloads
 * them. A failure to load is not an error state for the page: the ads still show, without
 * pictures.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { auth, functions } from '../firebase';
import { AnalyticsRepository } from '../models/AnalyticsRepository';

const EMPTY = { images: {}, texts: {} };
let cached = null;
// One attempt per tab load. A failed read is not retried on every mount: the callable SDK
// already retries, and a page that re-mounts often would turn one failure into a stream.
let attempted = false;

export function useAdImages() {
  const repo = useMemo(() => new AnalyticsRepository(functions), []);
  const [store, setStore] = useState(cached || EMPTY);
  const [loaded, setLoaded] = useState(!!cached);

  useEffect(() => {
    if (cached || attempted) { setLoaded(true); return undefined; }
    // ⚠️ Never call without a signed-in colleague. The dev preview (/dev/analytics) renders
    // this page signed out, and an unauthenticated call to a live function is refused AND
    // counted as an error on the System Health board.
    if (!auth || !auth.currentUser) { setLoaded(true); return undefined; }
    attempted = true;
    let live = true;
    repo.getAdImages()
      .then((got) => { cached = got; if (live) setStore(got); })
      .catch(() => { /* pictures are optional — the page says when an ad has none */ })
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, [repo]);

  // Save one field of one ad and update the copy every page shares. '' removes the field.
  const save = useCallback(async (key, field, value) => {
    await repo.setAdDetails(key, { [field]: value });
    const base = cached || EMPTY;
    const group = field === 'image' ? 'images' : 'texts';
    const next = { ...base, [group]: { ...base[group] } };
    if (value) next[group][key] = value; else delete next[group][key];
    cached = next;
    setStore(next);
  }, [repo]);
  const setImage = useCallback((key, image) => save(key, 'image', image), [save]);
  const setText = useCallback((key, text) => save(key, 'text', text), [save]);

  return { images: store.images, texts: store.texts, loaded, setImage, setText };
}

/** For tests only. */
export function resetAdImagesCache() { cached = null; attempted = false; }

export default useAdImages;
